import { NO_CATEGORY } from '@shared/constants/categories';
import { CountryInfoWithCurrency } from '@shared/types/country-info';
import { OfferType } from '@shared/types/offer-type';
import { CurrencyType } from '@shared/types/offers';
import clsx from 'clsx';
import { PowerFiatSwitcher } from 'components/power-fiat-switcher/powerFiatSwithcer';
import { icons } from 'constants/assets';
import { useTranslation } from 'i18n/useTranslation';
import {
  FC, ReactNode, useCallback, useState,
} from 'react';
import { useTranslation as useTranslationHook } from 'react-i18next';

const SCROLL_TIMEOUT = 100;

type HeaderCategoryItemProps = {
  children?: ReactNode;
  isActive?: boolean;
  onClick: VoidFunction;
  isGuildCategoriesVisible?: boolean;
  isRegularCategoriesVisible?: boolean;
}

const HeaderCategoryItem: FC<HeaderCategoryItemProps> = ({
  children,
  isActive = false,
  onClick,
  isGuildCategoriesVisible,
  isRegularCategoriesVisible,
}) => (
  <button
    type="button"
    className={clsx('poppins-md border-b-separator flex cursor-pointer items-center justify-between border-b p-4', isActive && 'text-neon_yellow')}
    onClick={onClick}
  >
    <span>{children}</span>
    <img
      className={clsx('transition-all duration-200', isGuildCategoriesVisible || isRegularCategoriesVisible ? 'rotate-0' : 'rotate-180')}
      src={isActive ? icons.chevronDownYellow : icons.chevronDown}
      alt="chevron-down"
    />
  </button>
);

const CategoryItem: FC<{ children?: ReactNode, onClick: VoidFunction }> = ({ children, onClick }) => (
  <button type="button" className="poppins-md border-b-separator flex items-start border-b p-4 pl-8" onClick={onClick}>
    {children}
  </button>
);

type CategoriesSideNavProps = {
  currentOfferType: OfferType;
  currentCurrency: CurrencyType;
  changeCurrency: (currency: CurrencyType) => void;
  categories: string[];
  groupCategories: string[];
  isGuildFounder?: boolean;
  selectedCountryInfo: CountryInfoWithCurrency;
  scrollToCategory: (category: string, offerType: OfferType) => void;
  updateCurrentOfferType: (offerType: OfferType) => void;
}

export const CategoriesSideNav: FC<CategoriesSideNavProps> = ({
  currentOfferType,
  currentCurrency,
  changeCurrency,
  categories,
  groupCategories,
  isGuildFounder = false,
  scrollToCategory,
  updateCurrentOfferType,
  selectedCountryInfo,
}) => {
  const { i18n } = useTranslationHook();
  const { buttonsTranslations } = useTranslation();
  const isActiveGuildOfferType = currentOfferType === 'guild';
  const isActiveRegularOfferType = currentOfferType === 'regular';
  const [isGuildCategoriesVisible, setIsGuildCategoriesVisible] = useState(true);
  const [isRegularCategoriesVisible, setIsRegularCategoriesVisible] = useState(true);

  const handleCategoryItemClick = useCallback((category: string, offerType: OfferType) => {
    updateCurrentOfferType(offerType);

    setTimeout(() => scrollToCategory(category, offerType), SCROLL_TIMEOUT);
  }, [scrollToCategory, updateCurrentOfferType]);

  const getCategoryName = (category: string) => (category === NO_CATEGORY
    ? i18n.t('offers.noCategory', { defaultValue: 'No Category' })
    : i18n.t(`offers.category.${category}`, { defaultValue: category }));

  return (
    <nav className="sticky top-0 z-10 hidden h-screen min-w-[320px] flex-col gap-2 border-r-2 border-r-[#252665] bg-[#10122199] p-4 sm:flex">
      <div
        className="flex min-h-0 flex-col gap-2 overflow-y-auto"
      >
        {isGuildFounder && !!groupCategories.length && (
          <>
            <HeaderCategoryItem
              isActive={isActiveGuildOfferType}
              onClick={() => {
                updateCurrentOfferType('guild');
                setIsGuildCategoriesVisible((prevValue) => !prevValue);
              }}
              isGuildCategoriesVisible={isGuildCategoriesVisible}
            >
              {buttonsTranslations.guildOffers}
            </HeaderCategoryItem>

            {isGuildCategoriesVisible && groupCategories.map((category) => (
              <CategoryItem
                key={category}
                onClick={() => handleCategoryItemClick(category, 'guild')}
              >
                {getCategoryName(category)}
              </CategoryItem>
            ))}
          </>
        )}

        {!!categories.length && (
          <HeaderCategoryItem
            isActive={isActiveRegularOfferType}
            onClick={() => {
              updateCurrentOfferType('regular');
              setIsRegularCategoriesVisible((prevValue) => !prevValue);
            }}
            isRegularCategoriesVisible={isRegularCategoriesVisible}
          >
            {buttonsTranslations.regularOffers}
          </HeaderCategoryItem>
        )}

        {isRegularCategoriesVisible && categories.map((category) => (
          <CategoryItem
            key={category}
            onClick={() => handleCategoryItemClick(category, 'regular')}
          >
            {getCategoryName(category)}
          </CategoryItem>
        ))}
      </div>
      <div className="sticky bottom-4 mt-auto">
        <PowerFiatSwitcher
          countryInfo={selectedCountryInfo}
          onCurrencyChange={changeCurrency}
          value={currentCurrency}
        />
      </div>
    </nav>
  );
};
